// The hardware and wiring a robot was configured with, as a file to keep beside
// its URDF.
//
// Configuring a robot is a dozen answers about a board and its pins, and none of
// them change between drops of the same folder. Saving them lets a later drop
// skip the panel: `readSavedConfig` looks for the file while the dropped folder
// is still in JS's hands -- `planRobotFromFolder` consumes it -- and
// `applyConfig` writes what it finds into the plan through the very setters the
// panel uses. That is the point of doing it here rather than in Rust: the file
// is checked by the rules that produced it (the board's pins, the actuator's
// wires, the joint's limits) instead of by a second copy of them.
//
// Nothing in a saved file is fatal. Anything that cannot be applied -- a joint
// the URDF no longer has, a board that is no longer offered -- comes back as a
// note, and the caller opens the panel prefilled with whatever did apply so the
// user only fixes the difference.

import {
  actuatorDriverOptions,
  actuatorTravelDegrees,
  DroppedFolder,
  RobotPlan,
} from "simulator";

// What a file claims to be, and the shape it claims to be in. The version is
// only bumped if `SavedConfig` changes in a way an older reader would misread.
const FORMAT = "robot-sim-config";
// 2 added `switches`. A version 1 file simply has none, which is what a robot
// configured before they existed had; a version 2 file read by an older page
// would quietly lose them, which is what the version is for.
//
// 3 replaced each joint's `degrees` -- the angle the joint was built at -- with
// `servoDegrees`, the reading its horn shows with that joint at zero. The two
// are different numbers for the same servo and there is no converting between
// them without knowing how the horn was mounted, which is exactly what the old
// format never recorded. Renaming the key rather than reusing it is what stops
// either side reading the other's number as its own.
//
// 4 added `cameras`. A robot whose URDF draws no camera link has none either
// way; one that does, saved by an older page, has no sensor recorded for it and
// opens the panel with that one question outstanding.
//
// 5 renamed `servoDegrees` to `servoDegreesAtZeroPose`. The number is the same
// one -- what changed is only that the key now says which pose it was read in,
// rather than leaving a reader to wonder whether it is the servo's range. It is
// renamed rather than reused for the reason 3 renamed 2's key: a key that has
// meant two things is worse than one a file simply does not have.
//
// 6 added `busId`, the address a serial bus servo answers to. A robot with no
// bus servo on it has none either way, and an older file that fitted one --
// which no file can have, since the actuator did not exist before this version
// -- would simply be addressed by the same 1, 2, 3 default the panel offers.
// The version is here so a page reading a *newer* file knows what it is missing.
//
// 7 added each camera's `viewRotationDegrees`, how far its view was turned. It
// changes nothing about the robot -- only how the panel draws the picture -- so
// a file without it is read as upright, and an older page reading a newer file
// loses nothing but the turn.
//
// 8 added `psram`, the PSRAM setting sketches are compiled with. A file without
// it is read as the board's default, which is what every robot saved before it
// was compiled with; an older page reading a newer file compiles without PSRAM,
// and a sketch that needs it finds none.
//
// 9 added `free`, a joint left with no actuator on purpose. Before it such a
// joint could not be configured at all, so no older file has one; an older page
// reading a newer file finds no actuator named on it and asks again.
//
// 10 added `rpm`, the speed a gearmotor was bought at -- which is its gearbox.
// No older file can have one, since the actuator did not exist before it; an
// older page reading a newer file does not offer the actuator and asks again.
//
// 11 took the DRV8833 off the gearmotor and made it a part of its own, shared
// by up to two motors: `drivers` lists them with their wiring, and a motor's
// joint names the one it is on by `driver` and `channel`. A version 10 file
// wired a bridge per motor under the joint, and those pins are not carried
// over -- which motors share a driver is a question the old file never
// answered -- so each of its motors comes back with no driver, and the panel
// opens to ask. Its encoder wiring still applies.
//
// 12 added `power`, what the robot runs on. A file without it is read as the
// first source on offer -- the 7.4V LiPo, the only one there has been -- so no
// older file is missing anything; an older page reading a newer file ignores it.
//
// 13 added `stepDown`, the converter taking that source down to the board's
// rail. A file without it is read as the first on offer -- the LM2596 at 5V,
// the only one there has been -- for the reason `power` is.
//
// 14 let a power wire's pin be a rail off the board -- the battery's or the
// converter's, saved by the name the wiring step shows (`LiPo 7.4V`, `LM2596
// 5V`) -- and stopped offering a power wire a rail it is not rated for. Nothing
// in the shape changed. An older file's MG90S on 3V3, or ST3215 on 5V, is no
// longer on a pin that wire is offered, so it comes back on a default -- the
// ST3215 on the LiPo -- and the panel opens to say so; an older page reading a
// newer file finds no such pin and does the same.
//
// The Nano's VIN went later with no version of its own, since nothing in the
// shape changed: a wire saved on it comes back on a default the same way.
const VERSION = 14;

// Downloads are saved under this name. A dropped folder is searched by suffix
// rather than for this exact name, because a browser that has already saved one
// calls the next `robot (1).sim.json` -- which is still the same file, and
// renaming it by hand is not something a user should have to know to do.
export const CONFIG_FILE = "robot.sim.json";
const CONFIG_SUFFIX = ".sim.json";

// How far a saved reading may sit outside the actuator's travel before it is
// worth mentioning. `setActuator` clamps silently, so without this an actuator
// with less travel than the one the file was written against would move the
// robot's calibration with no explanation. A tenth of a degree is the rounding
// the panel itself shows.
const ANGLE_SLACK = 0.1;

// A joint left free is saved as its name and `free: true` and nothing else --
// it has no actuator, so none of the fields below it would mean anything.
interface SavedFreeJoint {
  name: string;
  free: true;
}

interface SavedJoint {
  name: string;
  // Only there to make `free` the discriminant; never written.
  free?: undefined;
  actuator: string;
  // Where the horn stands with this joint at its URDF zero, in the actuator's
  // own degrees -- a calibration, not a pose and not a range. Absent in a file
  // older than 5, which recorded it under `servoDegrees` (or, before 3, the
  // joint's own angle under `degrees`). Absent too for an actuator with no horn
  // to read -- a gearmotor -- which `actuatorTravelDegrees` says by giving no
  // travel.
  servoDegreesAtZeroPose?: number;
  // The address this servo answers to on the bus it shares, for an actuator
  // that is on one. Absent for a servo commanded by a pulse, which is addressed
  // by the pin its signal wire is in and so is covered by `wiring` below.
  busId?: number;
  // The speed a gearmotor was bought at, in rpm at its rated voltage -- which is
  // what decides its gear ratio and its encoder's counts per turn. Absent for
  // an actuator that comes in one version.
  rpm?: number;
  // The driver a gearmotor is wired through, as an index into `drivers`, and
  // the channel of it by the name the part gives it. Absent for an actuator
  // that needs no driver.
  driver?: number;
  channel?: string;
  // Keyed by wire name rather than an array, so a wire is identified by what it
  // is and not by where it sat in a list the actuator is free to reorder.
  wiring: Record<string, string>;
}

// A part on the board rather than on a joint. Unlike a joint it has no name of
// its own to be keyed by -- the user picked one, and may well have picked the
// same one twice -- so these are matched by order: the file lists what to add,
// and adding them in that order is what reproduces the board.
interface SavedComponent {
  kind: string;
  name: string;
  wiring: Record<string, string>;
}

// A motor driver. Unlike a component the file does not add these for their own
// sake: one comes into being for the first motor that names it, and a driver no
// motor names is not built at all. Its wiring covers the shared wires and the
// inputs of each channel a motor is on.
interface SavedDriver {
  kind: string;
  name: string;
  wiring: Record<string, string>;
}

// The sensor fitted to a camera link. Keyed by the link's name, as a joint is
// by its own: the URDF decides how many there are and what they are called, so
// unlike a switch there is nothing here the file itself brings into existence.
interface SavedCamera {
  link: string;
  model: string;
  // Degrees clockwise the camera panel draws this link's view at: 0, 90, 180
  // or 270. The panel's, not the sensor's -- the board's frames are never
  // turned. Absent in a file older than 7.
  viewRotationDegrees?: number;
}

interface SavedConfig {
  format: string;
  version: number;
  robot: string;
  microcontroller: string;
  // Absent in a file older than 8, and for a board with no PSRAM setting.
  psram?: string;
  // Absent in a file older than 12, which ran on the first source on offer.
  power?: string;
  // Absent in a file older than 13, which had the first converter on offer.
  stepDown?: string;
  joints: (SavedJoint | SavedFreeJoint)[];
  // Absent in a version 1 file, and in any board that had none.
  switches?: SavedComponent[];
  // Absent in a file older than 11, and in any robot with no gearmotor.
  drivers?: SavedDriver[];
  // Absent in a file older than 4, and in any robot with no camera link.
  cameras?: SavedCamera[];
}

function depth(path: string): number {
  return path.split("/").length;
}

// Every file in a dropped folder whose path ends in `suffix`, shallowest first.
//
// The rule the URDF is picked under, applied to everything the page fishes out
// of a drop: a file at the top of the drop describes the robot that was
// dropped, one further down belongs to a sub-assembly. `paths()` comes back
// sorted and `sort` is stable, so equal depths break alphabetically.
//
// Anything using this must be called before the folder is handed to
// `planRobotFromFolder`, which takes ownership of it.
export function filesBySuffix(
  folder: DroppedFolder,
  suffix: string,
): string[] {
  return folder
    .paths()
    .filter((path) => path.toLowerCase().endsWith(suffix))
    .sort((a, b) => depth(a) - depth(b));
}

// The first of those, for the things a drop can only sensibly have one of.
export function shallowestFile(
  folder: DroppedFolder,
  suffix: string,
): string | undefined {
  return filesBySuffix(folder, suffix)[0];
}

// The saved configuration in a dropped folder, if it holds one.
export function readSavedConfig(folder: DroppedFolder): string | undefined {
  const found = shallowestFile(folder, CONFIG_SUFFIX);
  return found === undefined ? undefined : folder.text(found);
}

// The plan's answers as a file, ready to download.
//
// Read off the plan rather than off the panel's controls: by the time this is
// called the panel may never have been shown at all, and the plan is what was
// actually applied in either case. Joints with no actuator are simply left out,
// unless they were left free on purpose; loading is what notices the rest are
// missing.
export function configFromPlan(plan: RobotPlan): string {
  const joints: (SavedJoint | SavedFreeJoint)[] = [];
  for (let j = 0; j < plan.jointCount; j++) {
    if (plan.isFree(j)) {
      joints.push({ name: plan.jointName(j) ?? `joint ${j}`, free: true });
      continue;
    }
    const actuator = plan.actuatorName(j);
    if (actuator === undefined) {
      continue;
    }
    // `undefined` for an actuator with no horn, and left out of the file.
    const degrees = plan.servoDegreesAtZeroPose(j);

    const wiring: Record<string, string> = {};
    for (let w = 0; w < plan.wireCount(j); w++) {
      const wire = plan.wireName(j, w);
      const pin = plan.wirePin(j, w);
      if (wire !== undefined && pin !== undefined) {
        wiring[wire] = pin;
      }
    }

    joints.push({
      name: plan.jointName(j) ?? `joint ${j}`,
      actuator,
      // Rounded because the stored angle is radians converted back, and four
      // decimal places of a degree is a thousand times finer than the servo can
      // be commanded. Reloading re-clamps it anyway.
      servoDegreesAtZeroPose:
        degrees === undefined ? undefined : Number(degrees.toFixed(4)),
      // `undefined` for a pulse servo, which JSON leaves the key out for
      // entirely -- the plan is what decides whether this joint has an address
      // at all, so there is nothing here to write when it says not.
      busId: plan.busId(j),
      // Likewise `undefined`, and left out, for anything but a gearmotor.
      rpm: plan.motorRpm(j),
      // And for a motor, the driver it is on. The plan numbers its drivers
      // as `drivers` below lists them, so the index carries straight over.
      driver: plan.motorDriver(j),
      channel: (() => {
        const d = plan.motorDriver(j);
        const c = plan.motorChannel(j);
        return d === undefined || c === undefined
          ? undefined
          : plan.driverChannelName(d, c);
      })(),
      wiring,
    });
  }

  const drivers: SavedDriver[] = [];
  for (let d = 0; d < plan.driverCount; d++) {
    const kind = plan.driverKind(d) ?? "";
    const wiring: Record<string, string> = {};
    for (let w = 0; w < plan.driverWireCount(d); w++) {
      const wire = plan.driverWireName(d, w);
      const pin = plan.driverWirePin(d, w);
      if (wire !== undefined && pin !== undefined) {
        wiring[wire] = pin;
      }
    }
    drivers.push({ kind, name: plan.driverName(d) ?? kind, wiring });
  }

  const switches: SavedComponent[] = [];
  for (let c = 0; c < plan.componentCount; c++) {
    const kind = plan.componentKind(c);
    if (kind === undefined) {
      continue;
    }

    const wiring: Record<string, string> = {};
    for (let w = 0; w < plan.componentWireCount(c); w++) {
      const wire = plan.componentWireName(c, w);
      const pin = plan.componentWirePin(c, w);
      if (wire !== undefined && pin !== undefined) {
        wiring[wire] = pin;
      }
    }

    switches.push({ kind, name: plan.componentName(c) ?? kind, wiring });
  }

  // A camera link with no sensor chosen is left out, like a joint with no
  // actuator: loading is what notices it is missing and asks again.
  const cameras: SavedCamera[] = [];
  for (let c = 0; c < plan.cameraCount; c++) {
    const link = plan.cameraLink(c);
    const model = plan.cameraModel(c);
    if (link !== undefined && model !== undefined) {
      cameras.push({ link, model });
    }
  }

  const config: SavedConfig = {
    format: FORMAT,
    version: VERSION,
    robot: plan.name,
    microcontroller: plan.microcontroller ?? "",
    // `undefined` for a board with no PSRAM setting, which JSON leaves out.
    psram: plan.psram,
    power: plan.power,
    stepDown: plan.stepDown,
    joints,
    switches,
    drivers,
    cameras,
  };
  return `${JSON.stringify(config, null, 2)}\n`;
}

// How far each camera's view was turned, by link, in a saved configuration.
//
// Read apart from `applyConfig` because the plan has nowhere to put it: the
// turn is the camera panel's, applied when the panel is pointed at the robot,
// long after the plan has been answered. A file that cannot be read has
// already said so through `applyConfig`, so it is simply no turns here.
export function readViewRotations(text: string): Map<string, number> {
  const rotations = new Map<string, number>();
  let config: SavedConfig;
  try {
    config = JSON.parse(text) as SavedConfig;
  } catch {
    return rotations;
  }
  if (config?.format !== FORMAT || !Array.isArray(config.cameras)) {
    return rotations;
  }
  for (const camera of config.cameras) {
    const degrees = camera?.viewRotationDegrees;
    if (typeof camera?.link === "string" && typeof degrees === "number") {
      rotations.set(camera.link, degrees);
    }
  }
  return rotations;
}

// A configuration from `configFromPlan` with the camera views' current turns
// written into it.
//
// The rest of the file is fixed when the robot is built, but the turn is
// changed from the panel whenever the user likes, so it is only known when the
// file is saved. A camera the file does not list -- one with no sensor, which
// the panel does not show either -- has no view to turn.
export function withViewRotations(
  text: string,
  rotations: Map<string, number>,
): string {
  const config = JSON.parse(text) as SavedConfig;
  for (const camera of config.cameras ?? []) {
    const degrees = rotations.get(camera.link);
    if (degrees !== undefined) {
      camera.viewRotationDegrees = degrees;
    }
  }
  return `${JSON.stringify(config, null, 2)}\n`;
}

// Write a saved configuration into a freshly read plan, returning what could not
// be applied. An empty list means the plan is fully configured and the panel has
// nothing left to ask.
export function applyConfig(plan: RobotPlan, text: string): string[] {
  const notes: string[] = [];

  // Parsed as `unknown` and then asserted, so the checks below are the ones
  // doing the work: this file came off a disk the page knows nothing about.
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return [`not readable JSON: ${error}`];
  }
  const config = parsed as SavedConfig;
  if (config?.format !== FORMAT) {
    return ["not a saved robot configuration"];
  }
  if (config.version > VERSION) {
    notes.push(
      `saved by a newer version of this page (${config.version}); ` +
        `read as far as it goes`,
    );
  }
  // A file older than 5 has no `servoDegreesAtZeroPose` in it, and neither key
  // it might have instead is read: 2 and earlier saved the joint's own angle
  // under `degrees`, which is a different number with no way back to this one,
  // and 3 and 4 saved this number under `servoDegrees`, which was renamed
  // rather than reused so that neither can be read as the other. One note for
  // the file rather than one per joint: it is the same sentence about all of
  // them, and the panel shows three notes.
  const staleAngles = !(config.version >= 5);
  if (staleAngles) {
    notes.push(
      "saved in an older format whose servo angles are not read; every " +
        "actuator is set to mid-travel and needs checking against the real " +
        "robot",
    );
  }

  // A board is needed before anything can be wired to it, but not before an
  // actuator can be fitted -- so a board that is no longer offered costs the
  // wiring and nothing else.
  let wirable = true;
  try {
    plan.setMicrocontroller(config.microcontroller);
  } catch (error) {
    notes.push(`${error}`);
    wirable = false;
  }
  // After the board, which starts it over at its default. A setting the board
  // does not offer costs only itself: the default is still a working build.
  if (wirable && typeof config.psram === "string") {
    try {
      plan.setPsram(config.psram);
    } catch (error) {
      notes.push(`${error}`);
    }
  }
  // Not tied to the board, so not held up by one that is no longer offered. A
  // source that is gone is a note, and the plan keeps the one it started on --
  // which, unlike a missing actuator, is still an answer.
  if (typeof config.power === "string") {
    try {
      plan.setPower(config.power);
    } catch (error) {
      notes.push(`${error}`);
    }
  }
  // Likewise the step-down converter.
  if (typeof config.stepDown === "string") {
    try {
      plan.setStepDown(config.stepDown);
    } catch (error) {
      notes.push(`${error}`);
    }
  }

  // The drivers, which are brought onto the board by the motors that name them
  // rather than in file order: one with no motor on it is not a part the plan
  // keeps. Each saved index maps to where its driver landed in the plan.
  const savedDrivers = Array.isArray(config.drivers) ? config.drivers : [];
  const driverAt = new Map<number, number>();

  const saved = new Map<string, SavedJoint | SavedFreeJoint>();
  for (const joint of Array.isArray(config.joints) ? config.joints : []) {
    if (joint && typeof joint.name === "string") {
      saved.set(joint.name, joint);
    }
  }

  for (let j = 0; j < plan.jointCount; j++) {
    const name = plan.jointName(j) ?? `joint ${j}`;
    const joint = saved.get(name);
    if (!joint) {
      notes.push(`joint \`${name}\` has no saved actuator`);
      continue;
    }
    saved.delete(name);

    if (joint.free === true) {
      plan.setFree(j);
      continue;
    }

    // Where the horn stands, which is the calibration and not a pose. The
    // travel it has to be inside belongs to the actuator the file names, so it
    // is asked for by name -- nothing is fitted to this joint yet.
    const travel = actuatorTravelDegrees(joint.actuator);
    const known = travel.length >= 2;
    let degrees = joint.servoDegreesAtZeroPose;
    if (!known) {
      // No horn to have read anything off, so nothing was saved and nothing is
      // asked for: `setActuator` still wants a number, and stores it unread.
      degrees = 0;
    } else if (typeof degrees !== "number" || !Number.isFinite(degrees)) {
      // Nothing usable to calibrate against -- an older file, or one edited by
      // hand. Mid-travel is the reading a horn is most often left at, and the
      // note above (or here, for a file that should have known better) is what
      // sends the user to look at it. Everything else in the file still
      // applies.
      degrees = (travel[0] + travel[1]) / 2;
      if (!staleAngles) {
        notes.push(
          `joint \`${name}\`: no saved servo angle; its actuator is set to ` +
            `${degrees.toFixed(1)}° and needs checking`,
        );
      }
    } else if (
      degrees < travel[0] - ANGLE_SLACK ||
      degrees > travel[1] + ANGLE_SLACK
    ) {
      // Clamped on the way in, so an actuator with less travel than the file
      // was written against moves the calibration under the user.
      notes.push(
        `joint \`${name}\`: saved servo angle ${degrees}° is outside the ` +
          `${travel[0].toFixed(1)}° to ${travel[1].toFixed(1)}° travel of ` +
          joint.actuator,
      );
    }

    try {
      plan.setActuator(j, joint.actuator, degrees);
    } catch (error) {
      notes.push(`joint \`${name}\`: ${error}`);
      continue;
    }

    // The address, for an actuator that has one. Fitting it just gave this
    // joint the lowest free address, so a file that says nothing leaves a
    // working default rather than an unaddressed servo -- and a file that names
    // one it cannot have costs that one number and nothing else.
    if (plan.busId(j) !== undefined && joint.busId !== undefined) {
      try {
        plan.setBusId(j, joint.busId);
      } catch (error) {
        notes.push(`joint \`${name}\`: ${error}`);
      }
    }

    // The speed, for a gearmotor, on the same terms: fitting it gave a default
    // speed, which a file that says nothing leaves standing, and a speed the
    // part is not sold at costs that one answer.
    if (plan.motorRpm(j) !== undefined && joint.rpm !== undefined) {
      try {
        plan.setMotorRpm(j, joint.rpm);
      } catch (error) {
        notes.push(`joint \`${name}\`: ${error}`);
      }
    }

    // The driver, for an actuator that needs one. A driver's first motor
    // brings it onto the board, on its first channel, and is then moved to the
    // channel the file says -- and the motors after it go on the channels it
    // has left. Its wiring is applied once every motor is on it, below, since
    // a channel's inputs only exist once a motor is on that channel.
    if (actuatorDriverOptions(joint.actuator).length > 0) {
      applyDriver(plan, j, name, joint, savedDrivers, driverAt, notes, config);
    }

    if (!wirable) {
      continue;
    }

    // Fitting the actuator just gave it default pins, so a wire the file says
    // nothing about stays on a working default rather than coming off the board.
    for (let w = 0; w < plan.wireCount(j); w++) {
      const wire = plan.wireName(j, w);
      const pin = wire === undefined ? undefined : joint.wiring?.[wire];
      if (pin === undefined) {
        notes.push(`joint \`${name}\`: the ${wire} wire has no saved pin`);
        continue;
      }
      try {
        plan.setWire(j, w, pin);
      } catch (error) {
        notes.push(`joint \`${name}\`: ${error}`);
      }
    }
  }

  for (const name of saved.keys()) {
    notes.push(`saved joint \`${name}\` is not in this robot`);
  }

  // The drivers' own wires, now that every motor that is going to be on one
  // is: a channel's inputs are only there to be wired once it has a motor.
  savedDrivers.forEach((part, at) => {
    const d = driverAt.get(at);
    if (d === undefined) {
      notes.push(
        `saved driver \`${part?.name ?? at}\` drives none of this robot's joints`,
      );
      return;
    }
    if (!wirable) {
      return;
    }
    const name = plan.driverName(d) ?? part.kind;
    for (let w = 0; w < plan.driverWireCount(d); w++) {
      const wire = plan.driverWireName(d, w);
      const pin = wire === undefined ? undefined : part.wiring?.[wire];
      if (pin === undefined) {
        notes.push(`\`${name}\`: the ${wire} wire has no saved pin`);
        continue;
      }
      try {
        plan.setDriverWire(d, w, pin);
      } catch (error) {
        notes.push(`\`${name}\`: ${error}`);
      }
    }
  });

  // The plan is freshly read and so has no components at all, which is what
  // makes adding them in file order enough to reproduce the board. A kind that
  // is no longer offered costs that one part and nothing else.
  for (const part of Array.isArray(config.switches) ? config.switches : []) {
    const kind = part?.kind;
    let index: number;
    try {
      index = plan.addComponent(kind);
    } catch (error) {
      notes.push(`${error}`);
      continue;
    }
    if (typeof part.name === "string" && part.name !== "") {
      plan.setComponentName(index, part.name);
    }
    const name = plan.componentName(index) ?? kind;
    if (!wirable) {
      continue;
    }

    // As for a joint's wires: adding the part gave it default pins, so a wire
    // the file says nothing about stays on one rather than coming off the
    // board.
    for (let w = 0; w < plan.componentWireCount(index); w++) {
      const wire = plan.componentWireName(index, w);
      const pin = wire === undefined ? undefined : part.wiring?.[wire];
      if (pin === undefined) {
        notes.push(`\`${name}\`: the ${wire} has no saved pin`);
        continue;
      }
      try {
        plan.setComponentWire(index, w, pin);
      } catch (error) {
        notes.push(`\`${name}\`: ${error}`);
      }
    }
  }

  // The cameras, matched by link name. A sensor is not wired to anything and
  // changes nothing about the robot, so an unreadable one costs that view and
  // nothing else -- but it is still a question the panel would otherwise have
  // to open for.
  const sensors = new Map<string, string>();
  for (const camera of Array.isArray(config.cameras) ? config.cameras : []) {
    if (camera && typeof camera.link === "string") {
      sensors.set(camera.link, camera.model);
    }
  }
  for (let c = 0; c < plan.cameraCount; c++) {
    const link = plan.cameraLink(c) ?? `camera ${c}`;
    const model = sensors.get(link);
    if (model === undefined) {
      notes.push(`camera link \`${link}\` has no saved sensor`);
      continue;
    }
    sensors.delete(link);
    try {
      plan.setCamera(c, model);
    } catch (error) {
      notes.push(`camera link \`${link}\`: ${error}`);
    }
  }
  for (const link of sensors.keys()) {
    notes.push(`saved camera link \`${link}\` is not in this robot`);
  }

  // The last word belongs to the same check the panel gates "add" on: a file
  // that puts two servos on one pin is applied and then reported, exactly as it
  // would be if it had been typed in.
  const problem = plan.wiringProblem();
  if (problem !== undefined) {
    notes.push(problem);
  }
  return notes;
}

// Put the motor on joint `j` on the driver and channel a saved file names for
// it, bringing the driver onto the board if this is the first motor to name it.
// What cannot be applied is a note, and leaves the motor with no driver --
// which `wiringProblem` then reports, and the panel opens to ask.
function applyDriver(
  plan: RobotPlan,
  j: number,
  name: string,
  joint: SavedJoint,
  savedDrivers: SavedDriver[],
  driverAt: Map<number, number>,
  notes: string[],
  config: SavedConfig,
) {
  const at = joint.driver;
  const part = typeof at === "number" ? savedDrivers[at] : undefined;
  if (at === undefined || part === undefined) {
    notes.push(
      config.version < 11
        ? `joint \`${name}\`: saved before motors had a driver of their own; ` +
            "choose one for it"
        : `joint \`${name}\` has no saved driver`,
    );
    return;
  }
  try {
    let d = driverAt.get(at);
    if (d === undefined) {
      d = plan.setNewMotorDriver(j, part.kind);
      if (typeof part.name === "string" && part.name !== "") {
        plan.setDriverName(d, part.name);
      }
      driverAt.set(at, d);
    }
    let channel: number | undefined;
    for (let c = 0; c < plan.driverChannelCount(d); c++) {
      if (plan.driverChannelName(d, c) === joint.channel) {
        channel = c;
      }
    }
    if (channel === undefined) {
      // A new driver has already put the motor on its first channel, which
      // stands; on one already there, the motor is left without.
      notes.push(
        `joint \`${name}\`: \`${plan.driverName(d)}\` has no channel ` +
          `\`${joint.channel}\``,
      );
      return;
    }
    plan.setMotorDriver(j, d, channel);
  } catch (error) {
    notes.push(`joint \`${name}\`: ${error}`);
  }
}

// Hand the configuration to the browser as a download.
export function downloadConfig(config: string) {
  const url = URL.createObjectURL(
    new Blob([config], { type: "application/json" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = CONFIG_FILE;
  link.click();
  // The click has already taken the data; the URL only has to outlive it, and
  // revoking on the next turn of the loop is late enough everywhere.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
